import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Lottie from "lottie-react";
import { useInView } from "react-intersection-observer";
import { Typography } from "../../general";
import { Container, Flex } from "../wrappers";
import { EliMediaPlayer } from "../../media";

type BackgroundMediaType = "image" | "video" | "lottie";



export interface DefaultSectionProps {
  title?: string | null | false;
  subtitle?: string | null | false;
  content?: React.ReactNode | null | false;
  actions?: React.ReactNode | null | false;
  alignment?: "left" | "center" | "right";
  backgroundMedia?: {
    type: BackgroundMediaType;
    src?: string;
    lottieOptions?: object;
    alt?: string;
    useMediaLoader?: boolean;
    mediaName?: string;
    mediaBaseUrl?: string;
  } ;
  textColor?: string;
  overlayColor?: string;
  backgroundColor?: string;
  fullHeight?: boolean;
  snap?: boolean;
  contentOverlay?: {
    color?: string;
    blur?: boolean;
    padding?: string;
    borderRadius?: string;
  } | null | false;
  sectionId?: string;
  shadow?: boolean;
  border?: boolean;
  stickyHeader?: React.ReactNode;
  stickyFooter?: React.ReactNode;
  parallax?: boolean;
  animateOnScroll?: boolean;
}

const SectionContainer = styled.section<{
  fullHeight?: boolean;
  snap?: boolean;
  backgroundColor?: string;
  shadow?: boolean;
  border?: boolean;
  isVisible?: boolean;
}>`
  position: relative;
  width: 100%;
  ${({ fullHeight }) => fullHeight && `height: 100vh;`}
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  scroll-snap-align: ${({ snap }) => (snap ? "start" : "none")};
  scroll-snap-type: ${({ snap }) => (snap ? "y mandatory" : "none")};
  background-color: ${({ backgroundColor }) => backgroundColor || "transparent"};
  ${({ shadow }) => shadow && `box-shadow: 0 4px 30px rgba(0,0,0,0.2);`}
  ${({ border }) => border && `border: 1px solid rgba(0,0,0,0.1);`}
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transform: ${({ isVisible }) => (isVisible ? 'translateY(0)' : 'translateY(40px)')};
  transition: all 0.6s ease-out;
`;

const MediaBackground = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  img,
  video,
  canvas {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
  }
  canvas {
    z-index: 0 !important;
  }
`;

const ParallaxMedia = styled(MediaBackground)`
  transform: translateY(var(--parallax-offset, 0px));
  will-change: transform;
  transition: transform 0.2s ease-out;
`;

const Overlay = styled.div<{ overlayColor?: string }>`
  position: absolute;
  inset: 0;
  background: ${({ overlayColor }) => overlayColor || "rgba(0, 0, 0, 0.4)"};
  z-index: 1;
`;

const ContentWrapper = styled(Container)`
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.lg};
`;

const OverlayCard = styled.div<{
  padding?: string;
  borderRadius?: string;
  blur?: boolean;
  color?: string;
}>`
  width: 100%;
  max-width: 800px;
  background: ${({ color }) => color || "rgba(255, 255, 255, 0.85)"};
  backdrop-filter: ${({ blur }) => (blur ? "blur(12px)" : "none")};
  padding: ${({ padding }) => padding || "2rem"};
  border-radius: ${({ borderRadius }) => borderRadius || "1rem"};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
  align-items: center;
  justify-content: center;
  text-align: center;
  max-height: 90vh;
  overflow-y: auto;
`;

export const DefaultSection: React.FC<DefaultSectionProps> = ({
  title,
  subtitle,
  content,
  actions,
  alignment = "center",
  backgroundMedia,
  textColor,
  overlayColor,
  backgroundColor,
  fullHeight = true,
  snap = true,
  contentOverlay,
  sectionId,
  shadow,
  border,
  stickyHeader,
  stickyFooter,
  parallax = false,
  animateOnScroll = false,
}) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!parallax) return;
    const handleScroll = () => {
      const offset = sectionRef.current?.getBoundingClientRect().top || 0;
      const factor = Math.min(Math.max(-offset * 0.2, -100), 100);
      sectionRef.current?.style.setProperty('--parallax-offset', `${factor}px`);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [parallax]);

  const mediaNode = (
    <>
      {backgroundMedia?.type === "image" && (
        <img src={backgroundMedia.src} alt={backgroundMedia.alt || "Background"} />
      )}
      {backgroundMedia?.type === "video" && backgroundMedia.useMediaLoader && backgroundMedia.mediaName && backgroundMedia.mediaBaseUrl ? (
        <EliMediaPlayer
          mediaName={backgroundMedia.mediaName}
          baseUrl={backgroundMedia.mediaBaseUrl}
          autoPlay
          muted
          loop
          hideNativeControls
          playsInline
          showXPNotifications={false}
        />
      ) : backgroundMedia?.type === "video" ? (
        <video autoPlay loop muted playsInline>
          <source src={backgroundMedia.src} type="video/mp4" />
        </video>
      ) : null}
      {backgroundMedia?.type === "lottie" && (
        <Lottie
          animationData={backgroundMedia.src}
          loop
          autoplay
          style={{ width: "100%", height: "100%" }}
          {...backgroundMedia.lottieOptions}
        />
      )}
    </>
  );

  return (
    <SectionContainer
      id={sectionId}
      ref={(el) => {
        ref(el);
        sectionRef.current = el as HTMLDivElement;
      }}
      fullHeight={fullHeight}
      snap={snap}
      backgroundColor={backgroundColor}
      shadow={shadow}
      border={border}
      isVisible={!animateOnScroll || inView}
    >
      {backgroundMedia && (parallax ? <ParallaxMedia>{mediaNode}</ParallaxMedia> : <MediaBackground>{mediaNode}</MediaBackground>)}

      {overlayColor && <Overlay overlayColor={overlayColor} />}

      <ContentWrapper center>
        {stickyHeader && (
          <div style={{ position: 'absolute', top: 0, width: '100%', zIndex: 3, padding: '1rem' }}>{stickyHeader}</div>
        )}

        {contentOverlay ? (
          <OverlayCard
            color={contentOverlay.color}
            blur={contentOverlay.blur}
            padding={contentOverlay.padding}
            borderRadius={contentOverlay.borderRadius}
          >
           

            {subtitle && <Typography color={textColor} as="span" variant="heading2" align="center">{subtitle}</Typography>}
            {title && <Typography color={textColor} as="span" variant="display" align="center">{title}</Typography>}
            {content && <Typography color={textColor} as="span" variant="heading3" align="center">{content}</Typography>}
            {actions && (
              <Flex gap="md" wrap="wrap" justify="center">
                {actions}
              </Flex>
            )}
                     

          </OverlayCard>
        ) : (
          <Container center>
            {subtitle && <Typography color={textColor} as="span" variant="heading2" align="center">{subtitle}</Typography>}
            {title && <Typography color={textColor} as="span" variant="display" align="center">{title}</Typography>}
            <Container style={{paddingTop:'1rem'}}>
            {content && <Typography color={textColor} as="span" variant="heading3" align="center">{content}</Typography>}
            {actions && (
              <Flex gap="md" wrap="wrap" justify="center">
                {actions}
              </Flex>
            )}
            </Container>
          </Container>
        )}

        {stickyFooter && (
          <div style={{ position: 'absolute', bottom: 0, width: '100%', zIndex: 3, padding: '1rem' }}>{stickyFooter}</div>
        )}
      </ContentWrapper>
    </SectionContainer>
  );
};